import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Onboarding checklist — shown on AppShell until completed or dismissed.
 * Computes step state from server (live, no extra DB writes needed).
 */
export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api("/org/onboarding").then(setData).catch(() => setHidden(true));
  }, []);

  if (!data || hidden || data.dismissed || data.completed) return null;

  const onCta = (path) => {
    if (path.startsWith("/app#")) {
      // Navigate inside the iframe (gestion.html). Pass hash via app route.
      nav("/app");
      // Postmessage to iframe to switch tab — best effort
      setTimeout(() => {
        const iframe = document.querySelector('iframe[data-testid="gestion-iframe"]');
        const tab = path.split("#")[1];
        if (iframe && iframe.contentWindow) {
          try { iframe.contentWindow.postMessage({ type: "navigate", tab }, "*"); } catch (e) { /* ignore */ }
        }
      }, 400);
    } else {
      nav(path);
    }
  };

  const onDismiss = async () => {
    if (!window.confirm("Masquer définitivement la checklist d'accueil ?")) return;
    try {
      await api("/org/onboarding/dismiss", { method: "POST" });
      setHidden(true);
    } catch (e) { /* silent */ }
  };

  const pct = Math.round((data.done / data.total) * 100);

  return (
    <div
      data-testid="onboarding-checklist"
      style={{
        maxWidth: 1200,
        margin: "20px auto 0",
        padding: "0 24px",
      }}
    >
      <div style={{
        background: "var(--surface, #fff)",
        border: "1px solid var(--border, #ddd)",
        borderRadius: "var(--radius-md, 12px)",
        padding: "20px 24px",
        boxShadow: "0 2px 8px rgba(11,79,92,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Bien démarrer</div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display, serif)", fontSize: 22, fontWeight: 500 }}>
              Configurez votre espace en 5 étapes
            </h3>
            <p style={{ margin: "4px 0 0", color: "var(--ink-muted)", fontSize: 13 }}>
              {data.done}/{data.total} étapes complétées · {pct}%
            </p>
          </div>
          <button
            onClick={onDismiss}
            data-testid="onboarding-dismiss"
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 12 }}
            aria-label="Masquer la checklist d'accueil"
          >
            Masquer
          </button>
        </div>

        <div style={{ height: 4, background: "var(--surface-2, #eee)", borderRadius: 2, overflow: "hidden", marginBottom: 16 }}>
          <div style={{
            height: "100%",
            width: `${pct}%`,
            background: "linear-gradient(90deg, var(--primary, #0B4F5C), var(--accent, #F26B5E))",
            transition: "width 400ms",
          }}></div>
        </div>

        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
          {data.steps.map((step) => (
            <li
              key={step.id}
              data-testid={`onboarding-step-${step.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                background: step.done ? "var(--surface-2, #f5f5f5)" : "transparent",
                border: step.done ? "1px solid transparent" : "1px solid var(--border, #ddd)",
                borderRadius: 8,
                opacity: step.done ? 0.7 : 1,
              }}
            >
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                borderRadius: "50%",
                flexShrink: 0,
                background: step.done ? "var(--ok, #10b981)" : "var(--surface-2, #eee)",
                color: step.done ? "#fff" : "var(--ink-muted, #666)",
                fontSize: 12,
              }}>
                {step.done ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <span style={{ fontWeight: 600 }}>{data.steps.findIndex(s => s.id === step.id) + 1}</span>
                )}
              </span>
              <span style={{ flex: 1, fontSize: 14, textDecoration: step.done ? "line-through" : "none", color: step.done ? "var(--ink-muted)" : "var(--ink)" }}>
                {step.label}
                {step.optional && !step.done && (
                  <span style={{ fontSize: 11, color: "var(--ink-muted)", marginLeft: 6 }}>· optionnel</span>
                )}
              </span>
              {!step.done && (
                <button
                  onClick={() => onCta(step.cta_path)}
                  data-testid={`onboarding-cta-${step.id}`}
                  className="btn btn-ghost btn-sm"
                  style={{ flexShrink: 0, fontSize: 12 }}
                >
                  {step.cta_label} →
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
